import React, { useEffect, useRef, useState } from "react";
import { theme } from "../../constants/StyledComponents";
import { IconSizeEnum } from "../../types/AppTypes";
import { IconManager } from "../core/IconManager/IconManager";
import { Tooltip } from "../core/Tooltip/Tooltip";
import {
  StyledIconSwitch,
  StyledIconSwitchItem,
  StyledSwitchBookmark,
} from "./IconSwitch.styled";

export type OptionItem<T> = {
  value: T;
  icon: React.ReactNode;
  tooltipLabel?: string;
};

type IconSwitchProps<T> = {
  options: OptionItem<T>[];
  selectedOption: T | null;
  setSelectedOption: (x: T) => void;
  disabled?: boolean;
};

export const IconSwitch = <T,>({
  options,
  selectedOption,
  setSelectedOption,
  disabled = false,
}: IconSwitchProps<T>) => {
  const [bookmarkStyle, setBookmarkStyle] = useState<{
    width: number;
    transform: string;
  }>({ width: 0, transform: "translateX(0px)" });
  const [allowTransitions, setAllowTransitions] = useState(false);

  const firstIcon = selectedOption === options[0].value;
  const lastIcon = selectedOption === options[options.length - 1].value;

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (selectedOption && itemRefs.current.length > 0) {
      const selectedIndex = options.findIndex(
        (opt) => opt.value === selectedOption,
      );
      const selectedElement = itemRefs.current[selectedIndex];

      if (selectedElement) {
        const { offsetWidth, offsetLeft } = selectedElement;
        if (firstIcon) {
          setBookmarkStyle({
            width: offsetWidth,
            transform: "translateX(0px)",
          });
        } else if (lastIcon) {
          setBookmarkStyle({
            width: offsetWidth,
            transform: `translateX(${offsetLeft + 2}px)`,
          });
        } else {
          setBookmarkStyle({
            width: offsetWidth,
            transform: `translateX(${offsetLeft}px)`,
          });
        }
        setTimeout(() => setAllowTransitions(true), 0);
      }
    }
  }, [selectedOption, options]);

  const isSelected = (option: OptionItem<T>): boolean => {
    return selectedOption !== null && selectedOption === option.value;
  };

  return (
    <StyledIconSwitch
      $firstIcon={firstIcon}
      $lastIcon={lastIcon}
      $disabled={disabled}
    >
      <StyledSwitchBookmark
        style={bookmarkStyle}
        $allowTransitions={allowTransitions}
      />
      {options.map((option, index) => {
        const isString = typeof option.icon === "string";
        const isActive = isSelected(option);
        const iconColor =
          isActive && disabled
            ? theme.colors.purple_3
            : isActive
              ? theme.colors.purple
              : disabled
                ? theme.colors.grey_3
                : theme.colors.black;

        const icon = (
          <IconManager size={IconSizeEnum.SMALL} color={iconColor}>
            {option.icon}
          </IconManager>
        );
        return (
          <StyledIconSwitchItem
            $disabled={disabled}
            $isActive={isActive}
            $isString={isString}
            key={`option-${option.value}`}
            onClick={() => !disabled && setSelectedOption(option.value)}
            ref={(el) => (itemRefs.current[index] = el)}
          >
            {option.tooltipLabel ? (
              <Tooltip message={option.tooltipLabel} autoWidth>
                {icon}
              </Tooltip>
            ) : (
              icon
            )}
          </StyledIconSwitchItem>
        );
      })}
    </StyledIconSwitch>
  );
};
